import React, { useCallback, useEffect, useState } from 'react';
import { Search, ScrollText, ChevronDown, ChevronRight } from 'lucide-react';
import { auditoriaService, RegistroAuditoria } from '../../services/auditoria';

const ROTULO_ACAO: Record<string, string> = {
  login: 'Entrou no sistema',
  logout: 'Saiu do sistema',
  login_falhou: 'Tentativa de acesso',
  login_bloqueado: 'Acesso bloqueado',
  criou: 'Criou',
  atualizou: 'Alterou',
  excluiu: 'Excluiu',
  excluiu_definitivo: 'Excluiu definitivamente',
  restaurou: 'Restaurou',
  ativou_usuario: 'Ativou usuário',
  desativou_usuario: 'Desativou usuário',
};

const COR_ACAO: Record<string, string> = {
  login: 'bg-blue-100 text-blue-800',
  logout: 'bg-gray-100 text-gray-700',
  login_falhou: 'bg-amber-100 text-amber-800',
  login_bloqueado: 'bg-red-100 text-red-800',
  criou: 'bg-green-100 text-green-800',
  atualizou: 'bg-indigo-100 text-indigo-800',
  excluiu: 'bg-orange-100 text-orange-800',
  excluiu_definitivo: 'bg-red-100 text-red-800',
  restaurou: 'bg-teal-100 text-teal-800',
  ativou_usuario: 'bg-green-100 text-green-800',
  desativou_usuario: 'bg-red-100 text-red-800',
};

const formatarValor = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return 'vazio';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  return String(v);
};

interface Props {
  /**
   * Empresas disponiveis. So o super admin recebe esta lista: para ele a tela
   * ganha o filtro e a coluna de empresa. Para o admin de empresa a trilha ja
   * vem restrita a propria empresa pelo backend, e a coluna seria uma repeticao.
   */
  empresas?: Array<{ id: number; nome: string }>;
}

const AuditoriaList: React.FC<Props> = ({ empresas }) => {
  const modoGlobal = Array.isArray(empresas);
  const [registros, setRegistros] = useState<RegistroAuditoria[]>([]);
  const [opcoes, setOpcoes] = useState<{ acoes: string[]; entidades: string[] }>({ acoes: [], entidades: [] });
  const [paginacao, setPaginacao] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [expandido, setExpandido] = useState<number | null>(null);

  const [busca, setBusca] = useState('');
  const [acao, setAcao] = useState('');
  const [entidade, setEntidade] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [pagina, setPagina] = useState(1);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await auditoriaService.listar({
        empresa_id: empresaId ? Number(empresaId) : undefined,
        search: busca || undefined,
        acao: acao || undefined,
        entidade: entidade || undefined,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
        page: pagina,
        per_page: 50,
      });
      setRegistros(res.data || []);
      setPaginacao(res.pagination || { current_page: 1, last_page: 1, total: 0 });
    } catch (e: any) {
      // Distingue falha de "nenhum registro": sem isso a tela vazia é ambígua.
      setErro(e?.response?.data?.message || 'Não foi possível carregar a trilha de auditoria.');
    } finally {
      setCarregando(false);
    }
  }, [busca, acao, entidade, dataInicio, dataFim, empresaId, pagina]);

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
  }, [carregar]);

  useEffect(() => {
    auditoriaService.opcoesDeFiltro()
      .then((r) => setOpcoes({ acoes: r.data?.acoes || [], entidades: r.data?.entidades || [] }))
      .catch(() => {});
  }, []);

  // Qualquer mudança de filtro volta para a primeira página: manter a página atual
  // mostraria "nenhum resultado" quando o novo filtro tem menos páginas.
  const aoFiltrar = (fn: (v: string) => void) => (valor: string) => { fn(valor); setPagina(1); };

  return (
    <div className="w-full space-y-4">
      <div>
        <div className="flex items-center gap-2">
          <ScrollText className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Trilha de auditoria</h1>
        </div>
        <p className="text-gray-600 mt-1">
          Registro de tudo que aconteceu no sistema: acessos, cadastros, alterações e exclusões.
        </p>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <div className={`grid grid-cols-1 gap-3 ${modoGlobal ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
          {modoGlobal && (
            <select value={empresaId} onChange={(e) => aoFiltrar(setEmpresaId)(e.target.value)} aria-label="Empresa"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="">Todas as empresas</option>
              {empresas!.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          )}
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <label htmlFor="busca-auditoria" className="sr-only">Buscar</label>
            <input id="busca-auditoria" value={busca} onChange={(e) => aoFiltrar(setBusca)(e.target.value)}
              placeholder="Buscar por usuário ou descrição"
              className="pl-9 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
          </div>
          <select value={acao} onChange={(e) => aoFiltrar(setAcao)(e.target.value)} aria-label="Ação"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Todas as ações</option>
            {opcoes.acoes.map((a) => <option key={a} value={a}>{ROTULO_ACAO[a] || a}</option>)}
          </select>
          <select value={entidade} onChange={(e) => aoFiltrar(setEntidade)(e.target.value)} aria-label="Registro"
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="">Todos os registros</option>
            {opcoes.entidades.map((en) => <option key={en} value={en}>{en}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="data-inicio" className="sr-only">Data inicial</label>
              <input id="data-inicio" type="date" value={dataInicio} onChange={(e) => aoFiltrar(setDataInicio)(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
            <div>
              <label htmlFor="data-fim" className="sr-only">Data final</label>
              <input id="data-fim" type="date" value={dataFim} onChange={(e) => aoFiltrar(setDataFim)(e.target.value)}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        {erro ? (
          <div role="alert" className="p-8 text-center">
            <p className="text-red-600 mb-3">{erro}</p>
            <button onClick={carregar} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Tentar novamente
            </button>
          </div>
        ) : carregando ? (
          <div className="p-10 text-center text-gray-500">Carregando...</div>
        ) : registros.length === 0 ? (
          <div className="p-10 text-center text-gray-500">Nenhum evento encontrado com os filtros atuais.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Quando</th>
                    {modoGlobal && <th className="text-left px-4 py-3 font-semibold">Empresa</th>}
                    <th className="text-left px-4 py-3 font-semibold">Quem</th>
                    <th className="text-left px-4 py-3 font-semibold">O que fez</th>
                    <th className="text-left px-4 py-3 font-semibold">Registro</th>
                    <th className="text-left px-4 py-3 font-semibold">Origem</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {registros.map((r) => {
                    const temDetalhe = r.alteracoes && Object.keys(r.alteracoes).length > 0;
                    const aberto = expandido === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <tr className={temDetalhe ? 'cursor-pointer hover:bg-gray-50' : ''}
                          onClick={() => temDetalhe && setExpandido(aberto ? null : r.id)}>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                            {new Date(r.created_at).toLocaleString('pt-BR')}
                          </td>
                          {modoGlobal && (
                            <td className="px-4 py-3 text-gray-700">
                              {empresas!.find((e) => e.id === r.empresa_id)?.nome
                                || (r.empresa_id ? `Empresa #${r.empresa_id}` : 'Sem empresa')}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{r.usuario_nome || '—'}</div>
                            <div className="text-gray-500">{r.usuario_email || '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${
                              COR_ACAO[r.acao] || 'bg-gray-100 text-gray-700'}`}>
                              {ROTULO_ACAO[r.acao] || r.acao}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            <div className="flex items-center gap-1">
                              {temDetalhe && (aberto ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                              {r.entidade ? `${r.entidade}${r.entidade_id ? ' #' + r.entidade_id : ''}` : '—'}
                            </div>
                            {r.descricao && <div className="text-gray-500">{r.descricao}</div>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.ip || '—'}</td>
                        </tr>
                        {aberto && temDetalhe && (
                          <tr className="bg-gray-50">
                            <td colSpan={modoGlobal ? 6 : 5} className="px-6 py-3">
                              <table className="text-xs w-full max-w-2xl">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="text-left py-1 font-medium">Campo</th>
                                    <th className="text-left py-1 font-medium">Antes</th>
                                    <th className="text-left py-1 font-medium">Depois</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(r.alteracoes!).map(([campo, v]: any) => (
                                    <tr key={campo} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-4 font-medium text-gray-700">{campo}</td>
                                      <td className="py-1.5 pr-4 text-red-700">{formatarValor(v?.de)}</td>
                                      <td className="py-1.5 text-green-700">{formatarValor(v?.para)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-600">
                {paginacao.total} evento{paginacao.total === 1 ? '' : 's'} · página {paginacao.current_page} de {paginacao.last_page}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setPagina((p) => Math.max(1, p - 1))} disabled={paginacao.current_page <= 1}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Anterior</button>
                <button onClick={() => setPagina((p) => p + 1)} disabled={paginacao.current_page >= paginacao.last_page}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Próxima</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditoriaList;
